import { collection, doc, documentId, getDoc, getDocs, query, where, updateDoc, setDoc } from 'firebase/firestore';
import { db, defaultDb } from '../firebase/config';

// Shapes based on Terra Daily data model (subset used in UI)
export interface TerraDailyDoc {
  provider?: string;
  user?: { reference_id?: string, provider?: string };
  metadata?: { start_time?: string; end_time?: string };
  steps_data?: {
    steps?: number;
    step_samples?: Array<{ timestamp: string; steps?: number }>;
  };
  distance_data?: {
    distance_meters?: number;
    distance_samples?: Array<{ timestamp: string; distance_meters?: number }>;
    // Some providers may embed steps here; keep optional for robustness
    steps?: number;
    step_samples?: Array<{ timestamp: string; steps?: number }>;
  };
  calories_data?: {
    total_burned_calories?: number;
    calorie_samples?: Array<{ timestamp: string; calories?: number }>;
  };
  oxygen_data?: any;
  heart_rate_data?: any;
}

export const terraDailyService = {
  async getDailyByPath(path: string): Promise<TerraDailyDoc | null> {
    try {
      const docRef = doc(defaultDb as any, path);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      const raw = snap.data() as TerraDailyDoc & { [key: string]: any };
      // Provider note: Terra daily docs commonly include provider under user.provider.
      // We normalize it to top-level `provider` for UI use, falling back to any existing provider field.
      const normalizedProvider = (raw?.user as any)?.provider ?? raw?.provider ?? (raw?.metadata as any)?.provider;
      return {
        ...raw,
        provider: normalizedProvider,
      } as TerraDailyDoc;
    } catch (err) {
      // Permission denied or other errors should be handled gracefully
      console.warn('[terraDailyService] Failed to fetch Terra daily doc:', path, err);
      return null;
    }
  },

  async getDailyForDay(paths: string[]): Promise<TerraDailyDoc[]> {
    if (!paths || paths.length === 0) return [];
    try {
      const results = await Promise.all(paths.map(p => this.getDailyByPath(p)));
      console.log('results', results);
      // Ensure provider normalization across all docs (in case of edge fetch paths)
      return results
        .filter((d): d is TerraDailyDoc => !!d)
        .map((d: any) => {
          const normalizedProvider = d?.provider ?? d?.user?.provider ?? d?.metadata?.provider;
          return { ...d, provider: normalizedProvider } as TerraDailyDoc;
        });
    } catch (err) {
      console.warn('[terraDailyService] Failed to fetch Terra daily docs for day:', err);
      return [];
    }
  },

  // Fallback: discover Terra daily docs for a date using user devices, then persist refs to day post
  async backfillWearableDayData(userId: string, dateID: string): Promise<{ docs: TerraDailyDoc[]; paths: string[] }> {
    const dateStr = `${dateID.substring(0,4)}-${dateID.substring(4,6)}-${dateID.substring(6,8)}`;
    try {
      // load device IDs from version2 users/{uid}/private/data
      const privateRef = doc(db, 'users', userId, 'private', 'data');
      const privateSnap = await getDoc(privateRef);
      const devices: { id: string; resource?: string; expired?: boolean }[] = privateSnap.exists() ? (privateSnap.data()?.deviceIDs || []) : [];
      const activeDevices = devices.filter(d => !d.expired && d.id);
      if (activeDevices.length === 0) {
        return { docs: [], paths: [] };
      }

      // query default DB for each device prefix
      const terraCol = collection(defaultDb as any, 'terra/2022-03-16/daily');
      const pathResults: string[] = [];
      const docResults: TerraDailyDoc[] = [];

      for (const dev of activeDevices) {
        const prefix = `${dev.id}-${dateStr}`;
        const q = query(
          terraCol,
          where(documentId(), '>=', prefix),
          where(documentId(), '<', prefix + '\uf8ff')
        );
        const snap = await getDocs(q);
        snap.forEach(s => {
          const p = `terra/2022-03-16/daily/${s.id}`;
          pathResults.push(p);
          docResults.push(s.data() as TerraDailyDoc);
        });
      }

      // Persist to version2 day post for future loads
      if (pathResults.length > 0) {
        const dayPostRef = doc(db, 'dayPosts', userId, 'posts', dateID);
        await setDoc(dayPostRef, { wearableDayData: pathResults }, { merge: true });
      }

      return { docs: docResults, paths: pathResults };
    } catch (err) {
      console.warn('[terraDailyService] backfillWearableDayData failed:', err);
      return { docs: [], paths: [] };
    }
  }
};


